import type { SessionListItem } from '@/features/sessions/types';
import { cn } from '@/lib/cn';
import {
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@heidi/design-system/page-shell';
import { typographyVariant } from '@heidi/design-system/typography';
import { FileText, ListPlus, MessageCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';
import { useOpenSessionFragment } from './use-open-session-fragment';
import { useSessionFragments } from './use-session-fragments';

interface FragmentRowProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  testId: string;
}

const FragmentRow = ({ icon, label, onClick, testId }: FragmentRowProps) => (
  <SidebarMenuSubItem>
    {/* p2 matches the session title above: the sub-button base is captionBold, and
        the size:'md' default resets only the size, leaving the row a weight heavier. */}
    <SidebarMenuSubButton
      asChild
      className={cn(
        typographyVariant({ variant: 'p2' }),
        'ms-8 w-[calc(100%-2.25rem)] text-start'
      )}
    >
      <button type="button" onClick={onClick} data-testid={testId} title={label}>
        {icon}
        <span className="min-w-0 flex-1 truncate text-start">{label}</span>
      </button>
    </SidebarMenuSubButton>
  </SidebarMenuSubItem>
);

interface RecentVisitClusterProps {
  session: SessionListItem;
}

/**
 * The inline fragment cluster for an expanded recent visit: Context (always —
 * it needs no data), the generated note, and up to three Ask-Heidi threads.
 * Mounted only while expanded, so the two fragment GETs fire lazily.
 */
export const RecentVisitCluster = ({ session }: RecentVisitClusterProps) => {
  const intl = useIntl();
  const sessionId = session.session_id;
  const { note, chats, isLoading } = useSessionFragments(sessionId);
  const { openContextTab, openNoteTab, openChat } = useOpenSessionFragment();

  return (
    <SidebarMenuSub className="mx-0 translate-x-0 gap-1.5 border-0 bg-transparent p-0 pb-1.5 shadow-none">
      <FragmentRow
        icon={<ListPlus />}
        label={intl.formatMessage(navV3Messages.context)}
        onClick={() => openContextTab(sessionId)}
        testId="sidebar-recent-visit-fragment-context"
      />
      {isLoading ? (
        <SidebarMenuSubItem>
          <SidebarMenuSkeleton />
        </SidebarMenuSubItem>
      ) : (
        <>
          {note && (
            <FragmentRow
              icon={<FileText />}
              label={note.heading ?? intl.formatMessage(navV3Messages.noteFallback)}
              onClick={() => openNoteTab(sessionId)}
              testId="sidebar-recent-visit-fragment-note"
            />
          )}
          {chats.map((chat) => (
            <FragmentRow
              key={chat.id}
              icon={<MessageCircle />}
              label={chat.title}
              onClick={() => openChat(chat)}
              testId="sidebar-recent-visit-fragment-chat"
            />
          ))}
        </>
      )}
    </SidebarMenuSub>
  );
};
